if (!customElements.get('multi-add-to-cart')) {
  class MultiAddToCart extends HTMLElement {
  // Delay before our /cart/add.js so the theme's form submits first.
  static THEME_ADD_DELAY_MS = 1000;

  connectedCallback () {
    this._abort = new AbortController();
    this._qtys = {};

    this.querySelectorAll('[id^="qty-"]').forEach(el => {
      this._qtys[el.id] = parseInt(el.textContent, 10) || 0;
    });

    this.addEventListener('click', this._handleClick.bind(this), { signal: this._abort.signal });
    this._hookProductForm();
    this._dispatch();
    this._syncMainFormQty();
  }

  disconnectedCallback () {
    this._abort?.abort();
  }

  /* ── Event handling ── */

  _handleClick (e) {
    const qtyBtn = e.target.closest('.multi-add__qty-btn');
    if (qtyBtn) return this._handleQty(qtyBtn);

    const addBtn = e.target.closest('.multi-add__add-btn');
    if (addBtn) return this._handleAdd(addBtn);

    // Click anywhere else on the row: if the add button is still visible, trigger it
    const row = e.target.closest('.multi-add__row');
    if (row) {
      const rowAddBtn = row.querySelector('.multi-add__add-btn');
      if (rowAddBtn && rowAddBtn.style.display !== 'none') return this._handleAdd(rowAddBtn);
    }
  }

  _handleAdd (btn) {
    const targetId = btn.dataset.target;
    const qtyEl = this.querySelector(`#${targetId}`);
    if (!qtyEl) return;

    this._setQty(targetId, qtyEl, 1);
    this._showQtySelector(btn, true);
  }

  _handleQty (btn) {
    const targetId = btn.dataset.target;
    const qtyEl = this.querySelector(`#${targetId}`);
    if (!qtyEl) return;

    const row = qtyEl.closest('.multi-add__row');
    const minQty = parseInt(row?.dataset.minQty, 10) || 0;

    const current = this._qtys[targetId] ?? 0;
    const next = btn.dataset.action === 'plus' ? current + 1 : Math.max(minQty, current - 1);

    if (next === current) return;

    this._setQty(targetId, qtyEl, next);

    if (next === 0) {
      const addButton = btn.closest('.multi-add__qty-wrapper')?.querySelector('.multi-add__add-btn');
      if (addButton) this._showQtySelector(addButton, false);
    }
  }

  /* ── Product form hook ── */

  // Match a form id (not element) — <product-rerender> may swap DOM nodes
  // on variant change. Scoped to <product-form> to skip <payment-terms>.
  _hookProductForm () {
    const section = this.closest('[id^="shopify-section-"]');
    if (!section) return;

    const form = section.querySelector('product-form form[action*="/cart/add"]');
    // `form.id` is shadowed by the <input name="id"> control, so read the attribute.
    if (!form?.getAttribute('id')) return;

    this._formId = form.getAttribute('id');
    document.addEventListener('submit', this._handleDocumentSubmit.bind(this), {
      signal: this._abort.signal,
      capture: true,
    });
  }

  _handleDocumentSubmit (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('id') !== this._formId) return;
    this._scheduleExtras(form);
  }

  // The theme form adds its own variant, but only with the form's quantity
  // (1 when there is no quantity input) — top up the difference when the
  // switcher row asks for more. Other variants are added in full.
  _scheduleExtras (form) {
    const formVariantId = form.querySelector('[name="id"]')?.value;
    const formQty = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;

    const items = [];
    this.getLines().forEach(line => {
      const id = parseInt(line.variantId, 10);
      if (String(line.variantId) === String(formVariantId)) {
        const topUp = line.qty - formQty;
        if (topUp > 0) items.push({ id, quantity: topUp });
        return;
      }
      items.push({ id, quantity: line.qty });
    });
    if (items.length === 0) return;

    setTimeout(() => {
      window.addToCart({ items }, true, false, true, false);
    }, MultiAddToCart.THEME_ADD_DELAY_MS);
  }

  /* ── State ── */

  _setQty (targetId, qtyEl, value) {
    this._qtys[targetId] = value;
    qtyEl.textContent = value;

    const row = qtyEl.closest('.multi-add__row');
    if (row) {
      row.classList.toggle('multi-add__row--selected', value > 0);
      this._updateRowPrice(row, value);
    }

    this._updateTotals();
    this._dispatch();
    this._syncMainFormQty();
  }

  // These add-to-cart forms have no quantity field, so the theme's native submit defaults to 1 — mirror the main variant's chosen quantity into a hidden field.
  _syncMainFormQty () {
    if (!this._formId) return;
    const form = document.getElementById(this._formId);
    const formVariantId = form?.querySelector('[name="id"]')?.value;
    if (!formVariantId) return;

    const mainLine = this.getLines().find(line => String(line.variantId) === String(formVariantId));
    const qty = Math.max(1, mainLine ? mainLine.qty : 1);

    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      if (qty <= 1) return;
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.appendChild(input);
    }
    input.value = qty;
  }

  _updateRowPrice (row, qty) {
    const price = parseInt(row.dataset.price, 10) || 0;
    const discount = parseFloat(row.dataset.discount) || 0;
    const unit = row.dataset.discountUnit || '%';
    const mainSharePrice = row.dataset.mainSharePrice ? parseInt(row.dataset.mainSharePrice, 10) : null;
    const soloDiscount = parseFloat(row.dataset.soloDiscountValue) || 0;

    // Only recompute for apply-main-share mode; other rows keep their per-unit display.
    if (mainSharePrice == null) return;

    const effectiveQty = Math.max(1, qty || 0);
    const lineFull = price * effectiveQty;
    let rowPrice = lineFull;

    // Allow negative deltas: when the main-share absorbs more discount than
    // this line is worth, the row should visibly reduce the summary total.
    if (discount > 0) {
      if (unit === '%') {
        // delta = M × (1 − pair_d/100) − S × (pair_d − solo_d)/100
        const complementaryShare = Math.round(lineFull * (100 - discount) / 100);
        const bonusPP = Math.max(0, discount - soloDiscount);
        const mainBonusCredit = Math.round(mainSharePrice * bonusPP / 100);
        rowPrice = complementaryShare - mainBonusCredit;
      } else {
        rowPrice = lineFull - discount * 100;
      }
    }

    const priceEl = row.querySelector('.multi-add__price');
    // The money formatter emits its own "-" for negative cents, so only prefix
    // "+" when the delta is positive.
    if (priceEl) priceEl.textContent = (rowPrice > 0 ? '+' : '') + this._formatMoney(rowPrice);

    const insteadOfEl = row.querySelector('.multi-add__price-instead-of');
    if (insteadOfEl) {
      const label = insteadOfEl.dataset.label || insteadOfEl.textContent.replace(/\s?[0-9].*$/, '').trim();
      insteadOfEl.dataset.label = label;
      insteadOfEl.textContent = `${label} ${this._formatMoney(lineFull)}`;
    }
  }

  /* ── UI ── */

  _showQtySelector (addBtn, show) {
    const wrapper = addBtn.closest('.multi-add__qty-wrapper');
    if (!wrapper) return;

    addBtn.style.display = show ? 'none' : '';
    wrapper.querySelector('.multi-add__qty').style.display = show ? '' : 'none';
  }

  /* ── Price calculation ── */

  _updateTotals () {
    let total = 0;
    let compareTotal = 0;

    this.querySelectorAll('.multi-add__row').forEach(row => {
      const price = parseInt(row.dataset.price, 10) || 0;
      const discount = parseFloat(row.dataset.discount) || 0;
      const unit = row.dataset.discountUnit || '%';
      const mainSharePrice = row.dataset.mainSharePrice ? parseInt(row.dataset.mainSharePrice, 10) : null;
      const qtyEl = row.querySelector('[id^="qty-"]');
      const qty = qtyEl ? (this._qtys[qtyEl.id] ?? 0) : 0;

      compareTotal += price * qty;
      total += this._applyLineDiscount({ price, qty, discount, discountUnit: unit, mainSharePrice });
    });

    const totalEl = this.querySelector('.multi-add__total-price');
    const compareEl = this.querySelector('.multi-add__total-compare');
    const badgeEl = this.querySelector('.multi-add__total .multi-add__badge--promo');

    if (totalEl) totalEl.textContent = this._formatMoney(total);
    if (compareEl) compareEl.textContent = this._formatMoney(compareTotal);
    if (badgeEl) badgeEl.textContent = '-' + this._formatMoney(compareTotal - total);
  }

  _applyLineDiscount (line) {
    const lineFull = line.price * line.qty;
    if (!line.discount) return lineFull;

    // No clamp: negative line deltas are intentional in main-share mode and
    // must propagate to totals so the summary reflects the true bundle price.
    if (line.discountUnit === '%') {
      if (line.mainSharePrice != null) {
        // delta = M × (1 − pair_d/100) − S × (pair_d − solo_d)/100
        const complementaryShare = Math.round(lineFull * (100 - line.discount) / 100);
        const bonusPP = Math.max(0, line.discount - (line.soloDiscount || 0));
        const mainBonusCredit = Math.round(line.mainSharePrice * bonusPP / 100);
        return complementaryShare - mainBonusCredit;
      }
      return Math.round(lineFull * (1 - line.discount / 100));
    }
    return lineFull - line.discount * 100;
  }

  _formatMoney (cents) {
    const amount = cents / 100;
    const hasCents = Math.round(cents) % 100 !== 0;
    return amount.toLocaleString('fr-FR', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    }) + '\u202f€';
  }

  /* ── Events ── */

  _dispatch () {
    this.dispatchEvent(new CustomEvent('multi-add-to-cart:change', {
      bubbles: true,
      composed: true,
      detail: { lines: this.getLines() }
    }));
  }

  /* ── Public API ── */

  getLines () {
    const lines = [];
    this.querySelectorAll('.multi-add__row').forEach(row => {
      const variantId = row.dataset.variantId;
      const qtyEl = row.querySelector('[id^="qty-"]');
      const qty = qtyEl ? (this._qtys[qtyEl.id] ?? 0) : 0;
      if (variantId && qty > 0) {
        const line = {
          variantId,
          qty,
          price: parseInt(row.dataset.price, 10) || 0,
          discount: parseFloat(row.dataset.discount) || 0,
          discountUnit: row.dataset.discountUnit || '%',
          productId: row.dataset.productId || '',
          productTitle: row.dataset.productTitle || '',
          variantTitle: row.dataset.variantTitle || '',
          sku: row.dataset.sku || '',
          vendor: row.dataset.vendor || '',
          category: row.dataset.category || '',
          url: row.dataset.url || '',
          image: row.dataset.productImage || ''
        };
        if (row.dataset.mainSharePrice) {
          line.mainSharePrice = parseInt(row.dataset.mainSharePrice, 10) || 0;
          line.soloDiscount = parseFloat(row.dataset.soloDiscountValue) || 0;
        }
        lines.push(line);
      }
    });
    return lines;
  }
}
  customElements.define('multi-add-to-cart', MultiAddToCart);
}
